import { logger } from '@tunasong/models'
import {Button, Alert} from '@tunasong/ui-lib'
import { type FC, type MouseEvent } from 'react'
import { usePermission } from './audio-permissions.hook.js'
import styles from './audio-permissions.module.css'

export interface AudioPermissionsProps {}

/**
 * Audio permissions component. Will check if we have permissions to use the audio input and output devices.
 * If not, we will show a message to the user to allow access to the devices.
 */

export const AudioPermissions: FC<AudioPermissionsProps> = props => {
  const {} = props

  const { permission: permissionStatus } = usePermission('microphone')

  const handleGrantAccess = (ev: MouseEvent) => {
    ev.preventDefault()
    /**
     * Ask the user for access to the microphone. Dismissing or denying the prompt is the user's choice, not an error:
     * the alert follows the permission status (GH #2919, TUNASONG-X9). The stream only asks: nothing uses it, so its
     * tracks stop at once and the mic indicator goes off. The permission stays granted.
     */
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => stream.getTracks().forEach(track => track.stop()))
      .catch(error => logger.warn('Microphone access not granted', error))
  }

  return (
    <>
      {permissionStatus === 'denied' && (
        <Alert severity="error" className={styles.alert}>
          You have denied access to the microphone. To use the microphone for audio input, please allow access to the
          microphone in your browser settings.
        </Alert>
      )}
      {permissionStatus === 'prompt' && (
        <Alert severity="warning" className={styles.alert}>
          Please allow Tunasong access to the microphone / input channels.
          <Button onClick={handleGrantAccess}>Grant Microphone access</Button>
        </Alert>
      )}
    </>
  )
}

export default AudioPermissions
